class Wishlist {
  static name = "wishlist";

  static classes = {
    EMPTY_GRID: "grid--empty",
    ITEM_WRAPPER: "grid__item",
    ICON_ACTIVE: "active",
  };

  static events = {
    UPDATED: `${this.name}:updated`,
  };

  static eventType = {
    ITEM_ADD: "add",
    ITEM_REMOVE: "remove",
  };

  /**
   * Retrieves the wishlist items from the local storage.
   *
   * @returns {Array} The wishlist items.
   */
  static getItems() {
    const wishlistData = localStorage.getItem(this.name);
    return wishlistData ? JSON.parse(wishlistData) : [];
  }

  static count = this.getItems().length;

  /**
   * Sets the wishlist items in the local storage and updates the wishlist
   * count.
   * Dispatches an event with the updated wishlist data.
   *
   * @param {Array} data - The wishlist items.
   * @param {Object} eventData - Additional event data passed to CustomEvent.
   * @throws {Error} If the wishlist data is not an array.
   */
  static setItems(data, eventData) {
    if (!Array.isArray(data)) {
      throw new Error("[Wishlist.setItems] Data should be an array");
    }

    localStorage.setItem(this.name, JSON.stringify(data));
    this.count = data.length;

    document.dispatchEvent(
      new CustomEvent(Wishlist.events.UPDATED, {
        detail: { wishlist: data, ...eventData },
      })
    );
  }

  /**
   * Toggles the item in the wishlist by adding or removing it based on its
   * current state.
   * Updates the wishlist items and dispatches an event with the updated
   * wishlist data.
   *
   * @param {string} handle - The product handle.
   * @throws {Error} If the product handle is missing.
   */
  static toggleItem(handle) {
    if (!handle) {
      throw new Error("[Wishlist.toggleItem] Missing product handle");
    }

    const items = new Set(this.getItems());
    const eventData = { type: Wishlist.eventType.ITEM_ADD, handle };

    if (items.has(handle)) {
      items.delete(handle);
      eventData.type = Wishlist.eventType.ITEM_REMOVE;
    } else {
      items.add(handle);
    }

    this.setItems(Array.from(items), eventData);
  }

  /**
   * Checks if an item is already added to the wishlist.
   *
   * @param {string} handle - The product handle.
   * @returns {boolean} True if the item is added, false otherwise.
   */
  static itemIsAdded(handle) {
    return this.getItems().includes(handle);
  }

  /**
   * Fetches the HTML content of a wishlist item card from the server.
   *
   * @param {string} handle - The product handle.
   * @returns {Promise<string>} The HTML content of the wishlist item card.
   * @throws {Error} If there is an error fetching the card.
   */
  static async fetchProductCard(handle) {
    try {
      const res = await fetch(`/products/${handle}?view=card`);
      if (!res.ok) throw new Error(res.status);
      const data = await res.text();
      return data;
    } catch (err) {
      throw new Error(
        `[Wishlist.fetchProductCard] Error while fetching "${handle}": ${err.message}`
      );
    }
  }

  /**
   * Fetches the HTML content of a wishlist item card from the server and
   * renders it as a DOM element.
   *
   * @param {string} handle - The product handle.
   * @returns {HTMLElement} - The rendered wishlist item card as a DOM element.
   * @throws {Error} - If there is an error rendering the wishlist item card.
   */
  static async renderItem(handle) {
    try {
      const data = await Wishlist.fetchProductCard(handle);
      const card = document.createElement("div");
      card.classList.add(Wishlist.classes.ITEM_WRAPPER);
      card.setAttribute("data-wishlist-handle", handle);
      card.innerHTML = data;

      return card;
    } catch (err) {
      throw new Error(
        `[Wishlist.renderItem] Error rendering "${handle}": ${err.message}`
      );
    }
  }
}

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute("handle");

    this.updateStatus();
    this.bindEvents();
  }

  updateStatus() {
    if (Wishlist.itemIsAdded(this.handle)) {
      this.classList.add(Wishlist.classes.ICON_ACTIVE);
    } else {
      this.classList.remove(Wishlist.classes.ICON_ACTIVE);
    }
  }

  bindEvents() {
    this.addEventListener("click", () => {
      Wishlist.toggleItem(this.handle);
    });

    document.addEventListener(Wishlist.events.UPDATED, () =>
      this.updateStatus()
    );
  }
}

customElements.define("wishlist-button", WishlistButton);

class WishlistCounter extends HTMLElement {
  constructor() {
    super();
    this.updateCount();
    this.bindEvents();
  }

  updateCount() {
    this.innerHTML = Wishlist.count || "";
  }

  bindEvents() {
    document.addEventListener(Wishlist.events.UPDATED, () =>
      this.updateCount()
    );
  }
}

customElements.define("wishlist-counter", WishlistCounter);

class WishlistGrid extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.bindEvents();
  }

  empty() {
    this.classList.add(Wishlist.classes.EMPTY_GRID);
    this.innerHTML = window.wishlistStrings.empty || "Empty wishlist";
  }

  prepare() {
    this.classList.remove(Wishlist.classes.EMPTY_GRID);
    this.innerHTML = "";
  }

  /**
   * Adds an item to the wishlist grid.
   *
   * @param {string} handle - The handle of the item to be added.
   */
  async addItem(handle) {
    const card = await Wishlist.renderItem(handle);

    // Add event listener to init breadcrumbs when needed
    card.querySelectorAll('.product-card-wrapper .card_product__link').forEach(el => {
      el.addEventListener('click', () => {
        const currentUrl = window.location.href;
        const breadcrumbs = JSON.parse(window.localStorage.getItem('breadcrumbs'));
        if (!currentUrl.endsWith(breadcrumbs[breadcrumbs.length - 1].url)) {
          window.localStorage.setItem('breadcrumbs', JSON.stringify([window.baseBreadcrumb]));
        }
      });
    });

    this.appendChild(card);
  }

  /**
   * Removes an item from the wishlist grid.
   *
   * @param {string} handle - The handle of the item to be removed.
   */
  removeItem(handle) {
    this.querySelector(`[data-wishlist-handle="${handle}"]`)?.remove();
  }

  init() {
    if (Wishlist.count === 0) {
      this.empty();
      return;
    }

    this.prepare();
    Wishlist.getItems().forEach((item) => {
      this.addItem(item);
    });
  }

  bindEvents() {
    document.addEventListener(Wishlist.events.UPDATED, (e) => {
      const { type, handle, wishlist } = e.detail;

      if (wishlist.length === 0) {
        this.empty();
      } else if (this.classList.contains(Wishlist.classes.EMPTY_GRID)) {
        this.prepare();
      }

      if (type === Wishlist.eventType.ITEM_ADD) {
        this.addItem(handle);
      } else if (type === Wishlist.eventType.ITEM_REMOVE) {
        this.removeItem(handle);
      }
    });
  }
}

customElements.define("wishlist-grid", WishlistGrid);
